/**
 * How an FCC party's name, address and phone number are written for display.
 *
 * Separate from `components/EntityCard` so both the card and the pages that show a
 * party inline (the client header's contact fallback) read from one definition.
 */
import type { FCCEntity } from '../types'

/** Organisation name if there is one, otherwise the person's assembled name. */
export function getEntityDisplayName(entity: FCCEntity): string {
  if (entity.entity_name) return entity.entity_name
  const parts = [
    entity.first_name,
    entity.middle_initial ? `${entity.middle_initial}.` : null,
    entity.last_name,
    entity.suffix,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : 'N/A'
}

/** Mailing address as display lines, empty ones dropped. */
export function getEntityAddress(entity: FCCEntity): string[] {
  return [
    entity.attention_line ? `ATTN: ${entity.attention_line}` : null,
    entity.po_box ? `PO Box ${entity.po_box}` : null,
    entity.street_address,
    [entity.city, entity.state, entity.zip_code].filter(Boolean).join(' '),
  ].filter((s): s is string => Boolean(s))
}

/** FCC stores bare digits; show them the way a phone number is written. */
export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return phone
}
